import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Avatar,
  IconButton,
  Chip,
  Tooltip,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Collapse,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";

import { useLazyGetImageQuery } from "@/api/services/profile/profile_/FetchImageApi";
import { StyledButton } from "@/components";
import InputAdornment from "@mui/material/InputAdornment";
import {
  useAddAllocationMutation,
  useGetEmployeeDataQuery,
  useRmOverallSubmitMutation,
  useGetProjectDataQuery,
  useUpdateAllocationMutation,
  useGetLeaveEmployeeQuery,
} from "@/api/services/resourceAllocation/resourceAllocation";
import { toast } from "@/components/toastify";

const STATUS_CONFIG = {
  pending: {
    label: "Pending",
    icon: <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} />,
    chipSx: { backgroundColor: "#FFF7ED", color: "#C2410C" },
    border: "1px solid #E2E8F0",
    avatarRing: "#E2E8F0",
    headerBg: "#F8FAFC",
    progressColor: "#F97316",
  },
  saved: {
    label: "Saved",
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,
    chipSx: { backgroundColor: "#EFF6FF", color: "#1D4ED8" },
    border: "1.5px solid #93C5FD",
    avatarRing: "#3B82F6",
    headerBg: "#EFF6FF",
    progressColor: "#3B82F6",
  },
  submitted: {
    label: "Submitted",
    icon: <DoneAllIcon sx={{ fontSize: 14 }} />,
    chipSx: { backgroundColor: "#D1FAE5", color: "#065F46" },
    border: "1.5px solid #6EE7B7",
    avatarRing: "#10B981",
    headerBg: "#ECFDF5",
    progressColor: "#10B981",
  },
};

const PROJECT_COLORS = [
  "#305533",
  "#4A7A96",
  "#475569",
  "#0D9488",
  "#64748B",
  "#5D8A66",
  "#3B82F6",
];

// ─── Employee Card (Accordion) ────────────────────────────────────────────────
const EmployeeCard = ({
  employee,
  profileUrl,
  status,
  onSave,
  onEdit,
  onSelect,
  expanded,
  onExpand,
  initialAllocations,
  isExisting,
  isLeaveInitial,
  isSelected,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [allocations, setAllocations] = useState(
    initialAllocations?.length > 0
      ? initialAllocations
      : [{ project: "", percentage: 0 }],
  );
  const [isLeave, setIsLeave] = useState(isLeaveInitial || false);
  const [duplicateIndexes, setDuplicateIndexes] = useState(new Set());
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    setIsLeave(isLeaveInitial || false);
  }, [isLeaveInitial]);

  useEffect(() => {
    if (initialAllocations?.length > 0) setAllocations(initialAllocations);
    else if (!isLeaveInitial) setAllocations([{ project: "", percentage: 0 }]);
  }, [initialAllocations, isLeaveInitial]);

  useEffect(() => {
    if (status === "pending") setIsEditing(true);
    else setIsEditing(false);
  }, [status]);

  const totalPercentage = allocations.reduce(
    (acc, c) => acc + (Number(c.percentage) || 0),
    0,
  );
  const isSubmitted = status === "submitted";
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const validAllocations = allocations.filter(
    (a) => a.project && a.percentage > 0,
  );

  const recalcDuplicates = (allocs) => {
    const dupes = new Set();
    allocs.forEach((a, i) => {
      if (!a.project.trim()) return;
      if (
        allocs.some(
          (b, j) =>
            j !== i &&
            b.project.trim().toLowerCase() === a.project.trim().toLowerCase(),
        )
      )
        dupes.add(i);
    });
    setDuplicateIndexes(dupes);
  };

  const handleAddProject = () => {
    setAllocations((prev) => [...prev, { project: "", percentage: 0 }]);
  };

  const handleRemoveProject = (index) => {
    const updated = allocations.filter((_, i) => i !== index);
    const result = updated.length ? updated : [{ project: "", percentage: 0 }];
    setAllocations(result);
    recalcDuplicates(result);
  };

  const handleChange = (index, field, value) => {
    const updated = [...allocations];
    if (field === "percentage") {
      let num = parseFloat(value);
      if (isNaN(num) || num < 0) num = 0;
      if (num > 100) num = 100;
      const otherTotal = allocations.reduce((acc, a, i) => i === index ? acc : acc + (Number(a.percentage) || 0), 0);
      if (otherTotal + num > 100) num = 100 - otherTotal;
      updated[index][field] = num;
    } else {
      updated[index][field] = value;
      if (field === "project" && value.trim()) {
        const isDup = updated.some(
          (a, i) =>
            i !== index &&
            a.project.trim().toLowerCase() === value.trim().toLowerCase(),
        );
        if (isDup)
          toast("warning", "This project is already assigned to this employee");
      }
      recalcDuplicates(updated);
    }
    setAllocations(updated);
  };

  const handleSaveClick = () => {
    if (!isLeave) {
      const errors = {};
      allocations.forEach((a, i) => {
        if (!a.project.trim()) errors[`p_${i}`] = "Required";
        if (!a.percentage) errors[`pct_${i}`] = "Required";
      });
      if (totalPercentage > 100) {
        toast("error", "Total allocation cannot exceed 100%");
        return;
      }
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
    }
    setFieldErrors({});
    setDuplicateIndexes(new Set());
    if (onSave) onSave(allocations, isLeave);
    setIsEditing(false);
    onExpand(null);
  };

  const handleCancelClick = () => {
    setAllocations(
      initialAllocations?.length > 0
        ? initialAllocations
        : [{ project: "", percentage: 0 }],
    );
    setIsLeave(isLeaveInitial || false);
    setFieldErrors({});
    setDuplicateIndexes(new Set());
    setIsEditing(false);
    onExpand(null);
  };

  return (
    <Card
      sx={{
        borderRadius: "14px",
        border: isSelected ? "2px solid #305533" : statusCfg.border,
        backgroundColor: "#fff",
        boxShadow: isSelected
          ? "0px 0px 0px 3px #30553318, 0px 4px 16px rgba(48,85,51,0.10)"
          : expanded
            ? "0px 4px 16px rgba(0,0,0,0.08)"
            : "0px 2px 8px rgba(0,0,0,0.05)",
        transition: "box-shadow 0.2s ease, border 0.2s ease",
        overflow: "visible",
        opacity: isSubmitted ? 0.88 : 1,
        "&:hover": {
          boxShadow: isSubmitted
            ? "0px 2px 8px rgba(0,0,0,0.05)"
            : "0px 4px 16px rgba(0,0,0,0.09)",
        },
      }}
    >
      {/* ── Accordion Header ── */}
      <Box
        onClick={() => {
          if (!isSubmitted) {
            onExpand();
            if (onSelect) onSelect();
          }
        }}
        sx={{
          px: 2,
          py: 1.2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          backgroundColor: statusCfg.headerBg,
          userSelect: "none",
          cursor: isSubmitted ? "default" : "pointer",
        }}
      >
        {/* Avatar with status ring */}
        <Box
          sx={{
            flexShrink: 0,
            p: "2px",
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${statusCfg.avatarRing}, ${statusCfg.avatarRing}55)`,
          }}
        >
          <Avatar
            src={profileUrl || undefined}
            alt={employee?.employeeName || employee?.name}
            sx={{
              width: 36,
              height: 36,
              border: "2px solid #fff",
              fontSize: "13px",
              fontWeight: 700,
              bgcolor: profileUrl ? "transparent" : statusCfg.avatarRing,
            }}
          >
            {!profileUrl &&
              (employee?.employeeName || employee?.name || "E")
                .charAt(0)
                .toUpperCase()}
          </Avatar>
        </Box>

        {/* Name + ID — never truncate, always full width */}
        <Box sx={{ minWidth: 160, flexShrink: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "13px",
              color: "#1E293B",
              lineHeight: 1.3,
              whiteSpace: "nowrap",
            }}
          >
            {employee?.employeeName || employee?.name || "Employee"}
          </Typography>
          <Typography
            sx={{
              fontSize: "11px",
              color: "#94A3B8",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
            }}
          >
            {employee?.empId || "—"}
            {(employee?.department || employee?.departmentName) && (
              <>
                {" "}
                ·{" "}
                <span style={{ color: "#64748B" }}>
                  {employee?.department || employee?.departmentName}
                </span>
              </>
            )}
          </Typography>
        </Box>

        {/* flex spacer */}
        <Box sx={{ flex: 1 }} />

        {/* on leave badge */}
        {/* {isLeave && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.4,
              flexShrink: 0,
            }}
          >
            <BeachAccessIcon sx={{ fontSize: 12, color: "#F97316" }} />
            <Typography
              sx={{ fontSize: "11px", fontWeight: 600, color: "#C2410C" }}
            >
              On Leave
            </Typography>
          </Box>
        )} */}

        {/* project count */}
        {!isLeave && validAllocations.length > 0 && (
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#64748B",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {validAllocations.length}{" "}
            {validAllocations.length === 1 ? "project" : "projects"}
          </Typography>
        )}

        {/* progress bar — small fixed width */}
        {!isLeave && (
          <Box
            sx={{
              width: 80,
              flexShrink: 0,
              height: 5,
              borderRadius: "4px",
              overflow: "hidden",
              backgroundColor: "#E2E8F0",
              display: "flex",
            }}
          >
            {validAllocations.length > 0 ? (
              validAllocations.map((a, i) => (
                <Tooltip
                  key={i}
                  title={`${a.project}: ${a.percentage}%`}
                  placement="top"
                  arrow
                >
                  <Box
                    sx={{
                      width: `${a.percentage}%`,
                      backgroundColor:
                        PROJECT_COLORS[i % PROJECT_COLORS.length],
                      borderRight:
                        i < validAllocations.length - 1
                          ? "1.5px solid #fff"
                          : "none",
                      transition: "width 0.3s",
                    }}
                  />
                </Tooltip>
              ))
            ) : (
              <Box
                sx={{
                  width: `${Math.min(totalPercentage, 100)}%`,
                  backgroundColor: PROJECT_COLORS[0],
                  transition: "width 0.3s",
                }}
              />
            )}
          </Box>
        )}

        {/* % label */}
        {!isLeave && (
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 800,
              flexShrink: 0,
              width: 32,
              textAlign: "right",
              color:
                totalPercentage >= 100 ? "#16A34A" : statusCfg.progressColor,
            }}
          >
            {totalPercentage}%
          </Typography>
        )}

        {/* Status chip */}
        <Chip
          size="small"
          icon={statusCfg.icon}
          label={statusCfg.label}
          sx={{
            ...statusCfg.chipSx,
            fontWeight: 700,
            fontSize: "10px",
            height: 22,
            flexShrink: 0,
          }}
        />

        {/* Expand indicator */}
        {!isSubmitted && (
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 26,
              height: 26,
              borderRadius: "7px",
              border: "1.5px solid #E2E8F0",
              backgroundColor: "transparent",
            }}
          >
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 16,
                color: "#64748B",
                transition: "transform 0.2s",
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </Box>
        )}
      </Box>

      {/* ── Accordion Body ── */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mx: 2, height: "1px", backgroundColor: "#F1F5F9" }} />

        <Box
          sx={{
            px: 2,
            pt: 1.5,
            pb: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {/* ── Top bar: column labels + On Leave toggle ── */}
          <Box sx={{ display: "flex", alignItems: "center", px: 0.5 }}>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                color: "#94A3B8",
                letterSpacing: "0.06em",
                flex: 1,
              }}
            >
              PROJECT
            </Typography>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                color: "#94A3B8",
                letterSpacing: "0.06em",
                width: 60,
                textAlign: "center",
              }}
            >
              ALLOCATION
            </Typography>
            {/* {isEditing && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={isLeave}
                    onChange={(e) => setIsLeave(e.target.checked)}
                    sx={{
                      p: 0.3,
                      color: "#CBD5E1",
                      "&.Mui-checked": { color: "#305533" },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                    <BeachAccessIcon sx={{ fontSize: 11, color: "#64748B" }} />
                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: 600,
                        color: "#64748B",
                      }}
                    >
                      On Leave
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, ml: 1.5 }}
              />
            )} */}
          </Box>

          {/* ── Leave banner ── */}
          {isLeave ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.2,
                py: 2.5,
                backgroundColor: "#FFF7ED",
                borderRadius: "10px",
                border: "1.5px dashed #FED7AA",
              }}
            >
              <BeachAccessIcon sx={{ fontSize: 20, color: "#F97316" }} />
              <Typography
                sx={{ fontWeight: 700, fontSize: "13px", color: "#C2410C" }}
              >
                Employee is on long leave
              </Typography>
            </Box>
          ) : (
            <>
              {/* ── Project list ── */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.7,
                  maxHeight: 240,
                  overflowY: "auto",
                  pr: 0.5,
                  "&::-webkit-scrollbar": { width: "4px" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#CBD5E1",
                    borderRadius: "3px",
                  },
                }}
              >
                {isEditing ? (
                  allocations.map((alloc, idx) => {
                    const color = PROJECT_COLORS[idx % PROJECT_COLORS.length];
                    const hasError =
                      duplicateIndexes.has(idx) || !!fieldErrors[`p_${idx}`];
                    return (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          borderRadius: "9px",
                          border: `1.5px solid ${hasError ? "#FCA5A5" : "#E8EDF2"}`,
                          backgroundColor: "#fff",
                          overflow: "hidden",
                          transition: "border-color 0.15s, box-shadow 0.15s",
                          "&:hover": {
                            borderColor: color,
                            boxShadow: `0 0 0 3px ${color}18`,
                          },
                        }}
                      >
                        {/* left color strip */}
                        <Box
                          sx={{
                            width: 3,
                            alignSelf: "stretch",
                            backgroundColor: color,
                            flexShrink: 0,
                          }}
                        />

                        {/* index dot */}
                        <Box
                          sx={{ px: 1, display: "flex", alignItems: "center" }}
                        >
                          <Box
                            sx={{
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              backgroundColor: `${color}18`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "9px",
                                fontWeight: 800,
                                color,
                                lineHeight: 1,
                              }}
                            >
                              {idx + 1}
                            </Typography>
                          </Box>
                        </Box>

                        {/* project name */}
                        <TextField
                          value={alloc.project}
                          onChange={(e) => {
                            if (e.target.value.includes(",")) return;
                            handleChange(idx, "project", e.target.value);
                            setFieldErrors((prev) => {
                              const n = { ...prev };
                              delete n[`p_${idx}`];
                              return n;
                            });
                          }}
                          placeholder="Project name"
                          size="small"
                          error={hasError}
                          helperText={
                            duplicateIndexes.has(idx)
                              ? "Duplicate"
                              : fieldErrors[`p_${idx}`] || ""
                          }
                          sx={{
                            flex: 1,
                            "& .MuiInputBase-input": {
                              fontSize: "12px",
                              py: "7px",
                              px: "4px",
                              fontWeight: 500,
                              color: "#1E293B",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                            "& .MuiInputBase-root": {
                              backgroundColor: "transparent",
                            },
                            "& .MuiFormHelperText-root": {
                              fontSize: "9px",
                              mt: 0.2,
                              ml: 0,
                              color: "#EF4444",
                            },
                          }}
                        />

                        {/* vertical rule */}
                        <Box
                          sx={{
                            width: "1px",
                            height: 26,
                            backgroundColor: "#E8EDF2",
                            flexShrink: 0,
                          }}
                        />

                        {/* percentage */}
                        <TextField
                          value={
                            alloc.percentage === 0
                              ? ""
                              : String(alloc.percentage)
                          }
                          onChange={(e) => {
                            handleChange(idx, "percentage", e.target.value);
                            setFieldErrors((prev) => {
                              const n = { ...prev };
                              delete n[`pct_${idx}`];
                              return n;
                            });
                          }}
                          placeholder="0"
                          size="small"
                          error={!!fieldErrors[`pct_${idx}`]}
                          inputProps={{ maxLength: 4 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Typography
                                  sx={{
                                    fontSize: "11px",
                                    color,
                                    fontWeight: 700,
                                    mr: 0.3,
                                  }}
                                >
                                  %
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            width: 75,
                            flexShrink: 0,
                            "& .MuiInputBase-input": {
                              fontSize: "13px",
                              py: "7px",
                              px: "6px",
                              fontWeight: 700,
                              color,
                              textAlign: "right",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                            "& .MuiInputBase-root": {
                              backgroundColor: "transparent",
                            },
                          }}
                        />

                        {/* delete */}
                        {allocations.length > 1 && (
                          <>
                            <Box
                              sx={{
                                width: "1px",
                                height: 26,
                                backgroundColor: "#E8EDF2",
                                flexShrink: 0,
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveProject(idx)}
                              sx={{
                                mx: 0.6,
                                p: 0.4,
                                color: "#CBD5E1",
                                borderRadius: "6px",
                                "&:hover": {
                                  color: "#EF4444",
                                  backgroundColor: "#FEF2F2",
                                },
                              }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    );
                  })
                ) : validAllocations.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      py: 3,
                      gap: 0.5,
                      backgroundColor: "#F8FAFC",
                      borderRadius: "10px",
                      border: "1px dashed #CBD5E1",
                    }}
                  >
                    <AccessTimeOutlinedIcon
                      sx={{ fontSize: 18, color: "#CBD5E1" }}
                    />
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#94A3B8",
                      }}
                    >
                      No allocations yet
                    </Typography>
                  </Box>
                ) : (
                  validAllocations.map((a, i) => {
                    const color = PROJECT_COLORS[i % PROJECT_COLORS.length];
                    return (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          borderRadius: "9px",
                          border: "1.5px solid #E8EDF2",
                          backgroundColor: "#fff",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: 3,
                            alignSelf: "stretch",
                            backgroundColor: color,
                            flexShrink: 0,
                          }}
                        />
                        <Box
                          sx={{ px: 1, display: "flex", alignItems: "center" }}
                        >
                          <Box
                            sx={{
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              backgroundColor: `${color}18`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "9px",
                                fontWeight: 800,
                                color,
                                lineHeight: 1,
                              }}
                            >
                              {i + 1}
                            </Typography>
                          </Box>
                        </Box>
                        <Tooltip
                          title={a.project}
                          arrow
                          disableHoverListener={a.project.length < 24}
                        >
                          <Typography
                            sx={{
                              flex: 1,
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#1E293B",
                              py: "8px",
                            }}
                            noWrap
                          >
                            {a.project}
                          </Typography>
                        </Tooltip>
                        <Box
                          sx={{
                            width: "1px",
                            height: 26,
                            backgroundColor: "#E8EDF2",
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            width: 60,
                            textAlign: "center",
                            fontSize: "13px",
                            fontWeight: 800,
                            color,
                          }}
                        >
                          {a.percentage}%
                        </Typography>
                      </Box>
                    );
                  })
                )}
              </Box>

              {/* ── Add Project row ── */}
              {isEditing ? (
                <Box
                  onClick={handleAddProject}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    px: 1.5,
                    py: 0.85,
                    borderRadius: "9px",
                    border: "1.5px dashed #CBD5E1",
                    cursor: "pointer",
                    color: "#64748B",
                    transition: "all 0.15s",
                    "&:hover": {
                      borderColor: "#305533",
                      color: "#305533",
                      backgroundColor: "#F2F7F4",
                    },
                  }}
                >
                  <AddCircleOutlineIcon sx={{ fontSize: 14 }} />
                  <Typography
                    sx={{ fontSize: "12px", fontWeight: 600, flex: 1 }}
                  >
                    Add Project
                  </Typography>
                </Box>
              ) : (
                !isSubmitted && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Box
                      onClick={() => {
                        setIsEditing(true);
                        if (onEdit) onEdit();
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.8,
                        px: 1.5,
                        py: 0.85,
                        borderRadius: "9px",
                        border: "1.5px dashed #93C5FD",
                        cursor: "pointer",
                        color: "#1D4ED8",
                        transition: "all 0.15s",
                        "&:hover": {
                          borderColor: "#1D4ED8",
                          backgroundColor: "#EFF6FF",
                        },
                      }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 14 }} />
                      <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                        Edit
                      </Typography>
                    </Box>
                    {totalPercentage < 100 && (
                      <Box
                        onClick={() => {
                          setIsEditing(true);
                          setAllocations((prev) => [
                            ...prev,
                            { project: "", percentage: 0 },
                          ]);
                          if (onEdit) onEdit();
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.8,
                          px: 1.5,
                          py: 0.85,
                          borderRadius: "9px",
                          border: "1.5px dashed #CBD5E1",
                          cursor: "pointer",
                          color: "#64748B",
                          transition: "all 0.15s",
                          "&:hover": {
                            borderColor: "#305533",
                            color: "#305533",
                            backgroundColor: "#F2F7F4",
                          },
                        }}
                      >
                        <AddCircleOutlineIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                          Add Project
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )
              )}
            </>
          )}

          {/* ── Footer ── */}
          {isEditing && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                pt: 1,
                borderTop: "1px solid #F1F5F9",
              }}
            >
              {!isLeave && (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      height: 3,
                      borderRadius: "3px",
                      backgroundColor: "#E2E8F0",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${Math.min(totalPercentage, 100)}%`,
                        backgroundColor:
                          totalPercentage === 100 ? "#16A34A" : "#F97316",
                        borderRadius: "3px",
                        transition: "width 0.25s, background-color 0.2s",
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 700,
                      flexShrink: 0,
                      color: totalPercentage === 100 ? "#16A34A" : "#F97316",
                    }}
                  >
                    {totalPercentage}%
                  </Typography>
                </Box>
              )}
              <Button
                onClick={handleCancelClick}
                size="small"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "12px",
                  color: "#94A3B8",
                  px: 1.5,
                  borderRadius: "7px",
                  "&:hover": { backgroundColor: "#F1F5F9", color: "#475569" },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveClick}
                variant="contained"
                size="small"
                disabled={!isLeave && (duplicateIndexes.size > 0 || totalPercentage > 100)}
                startIcon={<SaveOutlinedIcon sx={{ fontSize: 13 }} />}
                sx={{
                  backgroundColor: "#305533",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "12px",
                  px: 2,
                  borderRadius: "8px",
                  boxShadow: "none",
                  "&:hover": { backgroundColor: "#1e3a21", boxShadow: "none" },
                  "&.Mui-disabled": {
                    backgroundColor: "#E2E8F0",
                    color: "#94A3B8",
                  },
                }}
              >
                {isExisting ? "Update" : "Save"}
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </Card>
  );
};

// ─── Submit Overlay ───────────────────────────────────────────────────────────
const SubmitOverlay = ({
  open,
  savedCount,
  totalCount,
  isSubmitting,
  onCancel,
  onConfirm,
}) => {
  if (!open) return null;
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 1300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(6px)",
        bgcolor: "rgba(255,255,255,0.4)",
        borderRadius: "16px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          p: 4,
          borderRadius: 3,
          textAlign: "center",
          bgcolor: "#fff",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        }}
      >
        <CheckCircleOutlineIcon
          sx={{ fontSize: 52, color: "success.main", mb: 1.5 }}
        />
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          Confirm Submission
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, lineHeight: 1.7 }}
        >
          You are about to submit allocations for <b>{savedCount}</b> of{" "}
          <b>{totalCount}</b> employees. This action will finalize their project
          assignments.
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onCancel}
            disabled={isSubmitting}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              borderColor: "#CBD5E1",
              color: "#64748B",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={onConfirm}
            disabled={isSubmitting}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              backgroundColor: "#305533",
              "&:hover": { backgroundColor: "#1e3a21" },
            }}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const ResourceAllocation = () => {
  const { data: empData, isLoading } = useGetEmployeeDataQuery();
  const { data: nonEligibleEmp } = useGetLeaveEmployeeQuery();
  const { data: projectData } = useGetProjectDataQuery();
  const employeeList = empData?.data || [];
  const projectList = projectData?.data || [];
  const [employeeStatuses, setEmployeeStatuses] = useState({});
  const [savedAllocations, setSavedAllocations] = useState({});
  const [submitModal, setSubmitModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addAllocation] = useAddAllocationMutation();
  const [updateAllocation] = useUpdateAllocationMutation();
  const [overallSubmit] = useRmOverallSubmitMutation();
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [nonEligibleModal, setNonEligibleModal] = useState(false);

  const [fetchImage] = useLazyGetImageQuery();
  const [imageMap, setImageMap] = useState({});
  const cachedImageMap = useRef({});

  const nonEligibleList = nonEligibleEmp?.data || [];

  useEffect(() => {
    const fetchImages = async () => {
      if (!employeeList?.length) return;
      const updatedMap = { ...cachedImageMap.current };
      await Promise.all(
        employeeList.map(async (emp) => {
          if (updatedMap[emp.empId]) return;
          try {
            if (
              emp.profilePicture?.fileName &&
              emp.profilePicture?.folderName
            ) {
              updatedMap[emp.empId] = await fetchImage({
                fileName: emp.profilePicture.fileName,
                folderName: emp.profilePicture.folderName,
              }).unwrap();
            }
          } catch (e) {}
        }),
      );
      cachedImageMap.current = updatedMap;
      setImageMap({ ...updatedMap });
    };
    if (employeeList.length > 0) fetchImages();
  }, [employeeList]);

  const projectMap = useMemo(() => {
    const map = {};
    projectList.forEach((p) => {
      map[p.empId] = p;
    });
    return map;
  }, [projectList]);

  useEffect(() => {
    if (projectList.length > 0) {
      const statuses = {};
      projectList.forEach((p) => {
        if (
          p.action?.toLowerCase() === "submit" ||
          p.rmStatus?.toLowerCase() === "submitted"
        )
          statuses[p.empId] = "submitted";
        else if (
          p.rmStatus?.toLowerCase() === "pending" ||
          p.action?.toLowerCase() === "save"
        )
          statuses[p.empId] = "saved";
        else statuses[p.empId] = "pending";
      });
      setEmployeeStatuses((prev) => ({ ...prev, ...statuses }));
    }
  }, [projectList]);

  const getStatus = (empId) => employeeStatuses[empId] || "pending";

  const handleSave = async (empId, allocations, isLeave) => {
    try {
      const payload = {
        empId,
        action: "Save",
        leaveStatus: isLeave ? "yes" : "no",
        projectDetails: isLeave
          ? []
          : allocations
              .filter((a) => a.project && a.percentage > 0)
              .map((a) => ({
                projectName: a.project,
                projectPercentage: a.percentage,
              })),
      };
      const existing = projectMap[empId];
      if (existing?.id) {
        await updateAllocation({ id: existing.id, ...payload }).unwrap();
        toast("success", "Allocation updated successfully");
      } else {
        await addAllocation(payload).unwrap();
        toast("success", "Allocation saved successfully");
      }
      setSavedAllocations((prev) => ({ ...prev, [empId]: allocations }));
      setEmployeeStatuses((prev) => ({ ...prev, [empId]: "saved" }));
    } catch (e) {
      toast("error", "Failed to save allocation");
    }
  };

  const handleEdit = (empId) =>
    setEmployeeStatuses((prev) => ({ ...prev, [empId]: "pending" }));

  const savedCount = Object.values(employeeStatuses).filter(
    (s) => s === "saved",
  ).length;
  const submittedCount = Object.values(employeeStatuses).filter(
    (s) => s === "submitted",
  ).length;
  const totalCount = employeeList.length;
  const allSaved =
    totalCount > 0 &&
    savedCount > 0 &&
    savedCount + submittedCount === totalCount;
  const allAreSaved = totalCount > 0 && savedCount === totalCount;

  const handleSubmitConfirm = async () => {
    setIsSubmitting(true);
    try {
      await overallSubmit("submit").unwrap();
      const updated = {};
      Object.keys(employeeStatuses).forEach((id) => {
        updated[id] =
          employeeStatuses[id] === "saved" ? "submitted" : employeeStatuses[id];
      });
      setEmployeeStatuses(updated);
      setSubmitModal(false);
    } catch (e) {
      toast("error", "Submit failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2.5,
        borderRadius: "16px",
        backgroundColor: "#ffffff",
        height: "88vh",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1E293B" }}>
            Resource Allocation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click the arrow on each employee card to assign projects and
            percentages.
          </Typography>
        </Box>
        {submittedCount < totalCount && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {!isLoading && totalCount > 0 && (
              <Chip
                label={`${savedCount + submittedCount} / ${totalCount} Saved`}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: "12px",
                  backgroundColor: allSaved ? "#DCFCE7" : "#FEF3C7",
                  color: allSaved ? "#16A34A" : "#D97706",
                }}
              />
            )}
            {nonEligibleList.length > 0 && (
              <Button
                size="small"
                onClick={() => setNonEligibleModal(true)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "12px",
                  borderRadius: "8px",
                  px: 1.5,
                  backgroundColor: "#FFF1F2",
                  color: "#BE123C",
                  "&:hover": { backgroundColor: "#FFE4E6" },
                }}
              >
                Not Eligible ({nonEligibleList.length})
              </Button>
            )}
            <Tooltip
              title={
                !allAreSaved
                  ? "All employees must be saved before submitting"
                  : "Submit all saved allocations"
              }
              arrow
            >
              <span>
                <StyledButton
                  label="Overall Submit"
                  variant="contained"
                  onClick={() => setSubmitModal(true)}
                  disabled={!allAreSaved}
                  sx={{
                    backgroundColor: allAreSaved ? "#305533" : undefined,
                    color: "#fff",
                    fontSize: "13px",
                    borderRadius: "8px",
                    "&:hover": { backgroundColor: "#1e3a21" },
                  }}
                />
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Body: 60% accordion list + 40% profile panel */}
      <Box sx={{ flex: 1, display: "flex", gap: 2, overflow: "hidden" }}>
        {/* 60% — Accordion list */}
        <Box
          sx={{
            flex: "0 0 60%",
            overflowY: "auto",
            overflowX: "hidden",
            pr: 1,
            pb: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#F1F5F9",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#CBD5E1",
              borderRadius: "3px",
            },
          }}
        >
          {isLoading ? (
            <Typography>Loading...</Typography>
          ) : employeeList.length > 0 ? (
            employeeList.map((emp, index) => (
              <EmployeeCard
                key={emp.empId || index}
                employee={emp}
                profileUrl={imageMap[emp.empId] || null}
                status={getStatus(emp.empId)}
                onSave={(allocs, isLeave) =>
                  handleSave(emp.empId, allocs, isLeave)
                }
                onEdit={() => handleEdit(emp.empId)}
                onSelect={() => setSelectedEmp(emp)}
                expanded={expandedId === (emp.empId || index)}
                onExpand={(close) =>
                  setExpandedId(
                    close === null
                      ? null
                      : expandedId === (emp.empId || index)
                        ? null
                        : emp.empId || index,
                  )
                }
                isSelected={selectedEmp?.empId === emp.empId}
                isExisting={!!projectMap[emp.empId]?.id}
                isLeaveInitial={projectMap[emp.empId]?.leaveStatus === "yes"}
                initialAllocations={
                  projectMap[emp.empId]?.projectDetails?.map((p) => ({
                    project: p.projectName,
                    percentage: Number(p.projectPercentage) || 0,
                  })) || []
                }
              />
            ))
          ) : (
            <Typography
              variant="body1"
              sx={{ textAlign: "center", mt: 6, color: "text.secondary" }}
            >
              No team members found.
            </Typography>
          )}
        </Box>

        {/* 40% — Profile panel */}
        <Box
          sx={{
            flex: "0 0 40%",
            borderRadius: "14px",
            border: "1px solid #E2E8F0",
            backgroundColor: "#F8FAFC",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: selectedEmp ? "flex-start" : "center",
            overflow: "hidden",
            p: 3,
            gap: 2,
          }}
        >
          {!selectedEmp ? (
            <Box sx={{ textAlign: "center", color: "#CBD5E1" }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  mx: "auto",
                  mb: 1.5,
                  bgcolor: "#E2E8F0",
                  fontSize: 28,
                }}
              >
                ?
              </Avatar>
              <Typography
                sx={{ fontSize: "13px", fontWeight: 600, color: "#94A3B8" }}
              >
                Select an employee
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "#CBD5E1", mt: 0.5 }}>
                Click any card to view profile
              </Typography>
            </Box>
          ) : (
            (() => {
              const emp = selectedEmp;
              const statusKey = getStatus(emp.empId);
              const cfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG.pending;
              const allocs =
                projectMap[emp.empId]?.projectDetails?.map((p) => ({
                  project: p.projectName,
                  percentage: Number(p.projectPercentage) || 0,
                })) || [];
              const validAllocs = allocs.filter(
                (a) => a.project && a.percentage > 0,
              );
              const total = validAllocs.reduce((s, a) => s + a.percentage, 0);
              const isOnLeave = projectMap[emp.empId]?.leaveStatus === "yes";
              return (
                <>
                  {/* Avatar */}
                  <Box
                    sx={{
                      p: "4px",
                      borderRadius: "50%",
                      background: `linear-gradient(135deg, ${cfg.avatarRing}, ${cfg.avatarRing}55)`,
                      mt: 1,
                    }}
                  >
                    <Avatar
                      src={imageMap[emp.empId] || undefined}
                      sx={{
                        width: 80,
                        height: 80,
                        border: "3px solid #fff",
                        fontSize: "28px",
                        fontWeight: 700,
                        bgcolor: cfg.avatarRing,
                      }}
                    >
                      {!imageMap[emp.empId] &&
                        (emp.employeeName || emp.name || "E")
                          .charAt(0)
                          .toUpperCase()}
                    </Avatar>
                  </Box>

                  {/* Name & info */}
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "15px",
                        color: "#1E293B",
                      }}
                    >
                      {emp.employeeName || emp.name}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "12px", color: "#94A3B8", mt: 0.3 }}
                    >
                      {emp.empId}
                    </Typography>
                    {(emp.department || emp.departmentName) && (
                      <Typography
                        sx={{ fontSize: "12px", color: "#64748B", mt: 0.2 }}
                      >
                        {emp.department || emp.departmentName}
                      </Typography>
                    )}
                    <Chip
                      size="small"
                      icon={cfg.icon}
                      label={cfg.label}
                      sx={{
                        ...cfg.chipSx,
                        fontWeight: 700,
                        fontSize: "10px",
                        height: 22,
                        mt: 1,
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      width: "100%",
                      height: "1px",
                      backgroundColor: "#E2E8F0",
                    }}
                  />

                  {/* Allocation summary */}
                  {isOnLeave ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* <BeachAccessIcon
                        sx={{ fontSize: 18, color: "#F97316" }}
                      />
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "13px",
                          color: "#C2410C",
                        }}
                      >
                        On Leave
                      </Typography> */}
                    </Box>
                  ) : validAllocs.length === 0 ? (
                    <Typography sx={{ fontSize: "12px", color: "#94A3B8" }}>
                      No allocations yet
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {/* segmented bar */}
                      <Box
                        sx={{
                          display: "flex",
                          height: 8,
                          borderRadius: "6px",
                          overflow: "hidden",
                          backgroundColor: "#E2E8F0",
                        }}
                      >
                        {validAllocs.map((a, i) => (
                          <Tooltip
                            key={i}
                            title={`${a.project}: ${a.percentage}%`}
                            placement="top"
                            arrow
                          >
                            <Box
                              sx={{
                                width: `${a.percentage}%`,
                                backgroundColor:
                                  PROJECT_COLORS[i % PROJECT_COLORS.length],
                                borderRight:
                                  i < validAllocs.length - 1
                                    ? "2px solid #fff"
                                    : "none",
                                transition: "width 0.3s",
                              }}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                          textAlign: "right",
                          color: total >= 100 ? "#16A34A" : "#F97316",
                        }}
                      >
                        {total}% allocated
                      </Typography>
                      {/* project list */}
                      {validAllocs.map((a, i) => (
                        <Box
                          key={i}
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              backgroundColor:
                                PROJECT_COLORS[i % PROJECT_COLORS.length],
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            sx={{
                              flex: 1,
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#1E293B",
                            }}
                            noWrap
                          >
                            {a.project}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: PROJECT_COLORS[i % PROJECT_COLORS.length],
                            }}
                          >
                            {a.percentage}%
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              );
            })()
          )}
        </Box>
      </Box>

      <Dialog
        open={nonEligibleModal}
        onClose={() => setNonEligibleModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <DialogTitle
          sx={{ fontWeight: 700, fontSize: "15px", color: "#1E293B", pb: 1 }}
        >
          Not Eligible Employees
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 400, mt: 0.3 }}
          >
            {nonEligibleList.length} employee
            {nonEligibleList.length !== 1 ? "s" : ""} not eligible for
            allocation
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {nonEligibleList.map((emp, i) => (
              <Box
                key={emp.empId || i}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2.5,
                  py: 1.2,
                  borderBottom:
                    i < nonEligibleList.length - 1
                      ? "1px solid #F1F5F9"
                      : "none",
                }}
              >
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    fontSize: "13px",
                    fontWeight: 700,
                    bgcolor: "#FFE4E6",
                    color: "#BE123C",
                  }}
                >
                  {(emp.employeeName || emp.name || "E")
                    .charAt(0)
                    .toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{ fontWeight: 600, fontSize: "13px", color: "#1E293B" }}
                    noWrap
                  >
                    {emp.employeeName || emp.name || "—"}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11px", color: "#94A3B8" }}
                    noWrap
                  >
                    {emp.empId}
                    {(emp.department || emp.departmentName) && (
                      <>
                        {" "}
                        ·{" "}
                        <span style={{ color: "#64748B" }}>
                          {emp.department || emp.departmentName}
                        </span>
                      </>
                    )}
                  </Typography>
                </Box>
                {(emp.reason || emp.leaveType) && (
                  <Chip
                    label={emp.reason || emp.leaveType}
                    size="small"
                    sx={{
                      fontSize: "10px",
                      fontWeight: 600,
                      backgroundColor: "#FFF1F2",
                      color: "#BE123C",
                      height: 20,
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button
            size="small"
            onClick={() => setNonEligibleModal(false)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 2.5,
              backgroundColor: "#305533",
              color: "#fff",
              "&:hover": { backgroundColor: "#1e3a21" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <SubmitOverlay
        open={submitModal}
        savedCount={savedCount}
        totalCount={totalCount}
        isSubmitting={isSubmitting}
        onCancel={() => !isSubmitting && setSubmitModal(false)}
        onConfirm={handleSubmitConfirm}
      />
    </Box>
  );
};

export default ResourceAllocation;
